import type { Edge, Node } from "@xyflow/react";
import type {
  AudienceFilterInputType,
  AutomationKindType,
  AutomationNodeMoveActionType,
  AutomationNodeTypeType,
  AutomationReentryModeType,
  AutomationRenewalFailureReasonType,
  AutomationTriggerType,
  AutomationWaitUnitType,
} from "@/types/graphql/partner.generated";
import type { ExitRuleForm } from "./exitRulesForm";

/** Шаг по вертикали: раскладка узла без позиции и нового шага под выбранным. */
export const NODE_STEP_Y = 160;

/**
 * Настройки события: тип и параметры. Какие параметры у какого события
 * имеют смысл, решает сервер (triggerRules.params); кабинет хранит все и
 * пишет все, пустые значения сервер не читает.
 */
export interface TriggerData {
  event: AutomationTriggerType | null;
  tagIds: string[];
  productIds: string[];
  contentIds: string[];
  daysBefore: number | null;
  reasons: AutomationRenewalFailureReasonType[];
}

/**
 * Данные шага: объект конфига своего типа (как узел GraphQL), остальные
 * конфиги null. Индексная сигнатура нужна типу Node из React Flow и
 * позволяет UI держать в data свои поля (подпись, подсветка ошибок,
 * статистика узла), но ни одно поле, кроме перечисленных здесь и
 * заполняемых через NodeTypeSpec.toInput, не попадает в
 * AutomationNodeInput (см. toAutomationInput) и не читается напрямую:
 * это защита от I1 (опасный сквозной проброс в мутацию сохранения).
 */
export interface AutomationNodeData extends Record<string, unknown> {
  type: AutomationNodeTypeType;
  trigger: TriggerData | null;
  sendEmail: { emailTemplateId: string | null } | null;
  wait: { value: number; unit: AutomationWaitUnitType } | null;
  condition: { filter: AudienceFilterInputType } | null;
  /**
   * true, если это узел типа, которого нет в NODE_TYPE_TABLE, или узел
   * известного типа, чьи серверные данные несут конфиг, которого таблица
   * не знает (более новая версия сервера). Такой узел кабинет показывает
   * как есть (тип и позиция сохраняются), но не умеет ни прочитать его
   * настройки, ни записать их обратно: toAutomationInput отказывается
   * строить вход мутации, пока такой узел есть в графе (см.
   * unsupportedNodeIds, UnsupportedAutomationNodesError).
   */
  unsupportedConfig?: boolean;
}

export type FlowNode = Node<AutomationNodeData, "automation">;
export type FlowEdge = Edge;

/**
 * Выбор партнёра для удалённого шага, на котором стоят зрители цепочки:
 * перевести их дальше или вывести. Применяется сервером при сохранении.
 */
export interface NodeMove {
  nodeId: string;
  action: AutomationNodeMoveActionType;
}

export interface AutomationFormState {
  /** Вид автоматизации: правила выхода, повторный вход и nodeMoves уходят в мутацию только у цепочки. */
  kind: AutomationKindType;
  name: string;
  viewersEnabled: boolean;
  audience: AudienceFilterInputType;
  contactListIds: string[];
  nodes: FlowNode[];
  edges: FlowEdge[];
  exitRules: ExitRuleForm[];
  reentry: { mode: AutomationReentryModeType; cooldownDays: number };
  nodeMoves: NodeMove[];
}

/** Шаг без удаления клавишей Delete: удаление только из панели, с подтверждением. */
export const flowNode = (
  id: string,
  data: AutomationNodeData,
  position: { x: number; y: number }
): FlowNode => ({
  id,
  type: "automation",
  position,
  data,
  deletable: false,
});
